// 📁 src/pages/FullMoon.js
import React, { useEffect, useMemo, useState } from "react";
import Seo from "../components/seo";
import { apiUrlWithLang, currentLanguage, withLanguage } from "../utils/locale";

/**
 * API:
 *   GET  /api/fullmoons?year=YYYY  -> { calendar[], next, previous, heroActive }
 *   POST /api/moon-blessing        -> { note }  // long 500-700 words
 */

/* ===============================
   THEME TOKENS
================================ */
const API_BASE =
  (typeof import.meta !== "undefined" && import.meta.env?.VITE_API_BASE) ||
  "https://tarotfate-api.tarotfate-com.workers.dev";

const WRAP = "px-[1vw]";
const MAXW = "max-w-10xl mx-auto";
const FONT = "font-[Cormorant_Garamond]";
const TEXT = "text-white/90";
const PANEL_BASE =
  "relative overflow-hidden rounded-2xl ring-1 ring-white/10 border border-white/10";

/* Canvas */
const CANVAS_BG = `
  radial-gradient(1200px 900px at 50% -12%, rgba(3,121,155,0.32), rgba(3,121,155,0) 40%),
  linear-gradient(180deg, #014C75 0%, #005C83 22%, #016280ff 38%, #0C4B84 72%, #014C75 90%, #821E3C 100%)
`;

/* ===============================
   Month palettes (0 = Jan ... 11 = Dec)
================================ */
const MOON_PALETTES = [
  { bg: "linear-gradient(135deg, #0A2E56E0, #123F71DB)", glow: "shadow-[0_12px_28px_rgba(118,175,255,0.18)]", ridge: "via-sky-200/70" },
  { bg: "linear-gradient(135deg, #2A183CE6, #182E5AE1)", glow: "shadow-[0_12px_28px_rgba(203,183,234,0.18)]", ridge: "via-fuchsia-200/70" },
  { bg: "linear-gradient(135deg, #0A4754E6, #0E223FE1)", glow: "shadow-[0_12px_28px_rgba(59,198,214,0.18)]",  ridge: "via-cyan-200/70" },
  { bg: "linear-gradient(135deg, #3C182CEB, #1C264EE1)", glow: "shadow-[0_12px_28px_rgba(255,149,214,0.18)]", ridge: "via-rose-200/70" },
  { bg: "linear-gradient(135deg, #0A413AE6, #103052E1)", glow: "shadow-[0_12px_28px_rgba(16,185,129,0.20)]",  ridge: "via-emerald-200/70" },
  { bg: "linear-gradient(135deg, #3A2A0CE6, #0F3655E1)", glow: "shadow-[0_12px_28px_rgba(250,204,21,0.20)]",  ridge: "via-amber-200/70" },
  { bg: "linear-gradient(135deg, #5C2018E6, #123760E1)", glow: "shadow-[0_12px_28px_rgba(255,140,66,0.20)]",  ridge: "via-orange-200/70" },
  { bg: "linear-gradient(135deg, #0C2C52E6, #10213DE6)", glow: "shadow-[0_12px_28px_rgba(118,141,255,0.18)]", ridge: "via-indigo-200/70" },
  { bg: "linear-gradient(135deg, #402A0AE6, #103051E6)", glow: "shadow-[0_12px_28px_rgba(245,158,11,0.22)]",  ridge: "via-yellow-200/70" },
  { bg: "linear-gradient(135deg, #421C10E6, #132E51E6)", glow: "shadow-[0_12px_28px_rgba(248,113,113,0.20)]", ridge: "via-red-200/70" },
  { bg: "linear-gradient(135deg, #242B24E6, #112D4CE6)", glow: "shadow-[0_12px_28px_rgba(147,197,114,0.18)]", ridge: "via-lime-200/70" },
  { bg: "linear-gradient(135deg, #09264FE6, #0D345FE6)", glow: "shadow-[0_12px_28px_rgba(186,221,255,0.18)]", ridge: "via-blue-200/70" },
];

/* Zodiac accents */
const Z_ACCENT = {
  Aries:        { chip: "bg-red-400/15 text-red-100 border-red-300/30",         bar: "via-red-200/70" },
  Taurus:       { chip: "bg-emerald-400/15 text-emerald-100 border-emerald-300/30", bar: "via-emerald-200/70" },
  Gemini:       { chip: "bg-yellow-400/15 text-yellow-100 border-yellow-300/30", bar: "via-yellow-200/70" },
  Cancer:       { chip: "bg-sky-400/15 text-sky-100 border-sky-300/30",         bar: "via-sky-200/70" },
  Leo:          { chip: "bg-amber-400/15 text-amber-100 border-amber-300/30",   bar: "via-amber-200/70" },
  Virgo:        { chip: "bg-lime-400/15 text-lime-100 border-lime-300/30",      bar: "via-lime-200/70" },
  Libra:        { chip: "bg-pink-400/15 text-pink-100 border-pink-300/30",      bar: "via-pink-200/70" },
  Scorpio:      { chip: "bg-fuchsia-400/15 text-fuchsia-100 border-fuchsia-300/30", bar: "via-fuchsia-200/70" },
  Sagittarius:  { chip: "bg-orange-400/15 text-orange-100 border-orange-300/30", bar: "via-orange-200/70" },
  Capricorn:    { chip: "bg-stone-400/15 text-stone-100 border-stone-300/30",   bar: "via-stone-200/70" },
  Aquarius:     { chip: "bg-cyan-400/15 text-cyan-100 border-cyan-300/30",      bar: "via-cyan-200/70" },
  Pisces:       { chip: "bg-indigo-400/15 text-indigo-100 border-indigo-300/30", bar: "via-indigo-200/70" },
};

/* ===============================
   Helpers
================================ */
const wc = (s) => (s || "").trim().split(/\s+/).filter(Boolean).length;

function zodiacForDate(d) {
  const m = d.getUTCMonth() + 1, day = d.getUTCDate();
  if ((m === 3 && day >= 21) || (m === 4 && day <= 19)) return "Aries";
  if ((m === 4 && day >= 20) || (m === 5 && day <= 20)) return "Taurus";
  if ((m === 5 && day >= 21) || (m === 6 && day <= 20)) return "Gemini";
  if ((m === 6 && day >= 21) || (m === 7 && day <= 22)) return "Cancer";
  if ((m === 7 && day >= 23) || (m === 8 && day <= 22)) return "Leo";
  if ((m === 8 && day >= 23) || (m === 9 && day <= 22)) return "Virgo";
  if ((m === 9 && day >= 23) || (m === 10 && day <= 22)) return "Libra";
  if ((m === 10 && day >= 23) || (m === 11 && day <= 21)) return "Scorpio";
  if ((m === 11 && day >= 22) || (m === 12 && day <= 21)) return "Sagittarius";
  if ((m === 12 && day >= 22) || (m === 1 && day <= 19)) return "Capricorn";
  if ((m === 1 && day >= 20) || (m === 2 && day <= 18)) return "Aquarius";
  return "Pisces";
}

const ZODIAC_ES = {
  Aries: "Aries",
  Taurus: "Tauro",
  Gemini: "Geminis",
  Cancer: "Cancer",
  Leo: "Leo",
  Virgo: "Virgo",
  Libra: "Libra",
  Scorpio: "Escorpio",
  Sagittarius: "Sagitario",
  Capricorn: "Capricornio",
  Aquarius: "Acuario",
  Pisces: "Piscis",
};

const MOON_NAME_ES = {
  "Wolf Moon": "Luna del Lobo",
  "Snow Moon": "Luna de Nieve",
  "Worm Moon": "Luna del Gusano",
  "Pink Moon": "Luna Rosa",
  "Flower Moon": "Luna de las Flores",
  "Strawberry Moon": "Luna de Fresa",
  "Buck Moon": "Luna del Ciervo",
  "Sturgeon Moon": "Luna del Esturion",
  "Harvest Moon": "Luna de la Cosecha",
  "Hunter's Moon": "Luna del Cazador",
  "Hunter\u2019s Moon": "Luna del Cazador",
  "Beaver Moon": "Luna del Castor",
  "Cold Moon": "Luna Fria",
  "Blue Moon": "Luna azul",
};

const MOON_MEANING_ES = {
  "A call to courage, community, and honest voice.": "Un llamado a la valentia, la comunidad y una voz honesta.",
  "Patience, rest, and steady inner work.": "Paciencia, descanso y trabajo interior sostenido.",
  "Small beginnings that soften the ground for growth.": "Pequenos comienzos que preparan el terreno para crecer.",
  "Tender renewal, friendship, and fresh perspective.": "Renovacion suave, amistad y una mirada fresca.",
  "Creative bloom, beauty, and shared joy.": "Creatividad en flor, belleza y alegria compartida.",
  "Gratitude, sweetness, and closing gentle loops.": "Gratitud, dulzura y cierres hechos con cuidado.",
  "Strength, confidence, and healthy boundaries.": "Fuerza, confianza y limites sanos.",
  "Depth, wisdom, and sustainable effort.": "Profundidad, sabiduria y esfuerzo sostenible.",
  "Gather results, honor labor, and share the table.": "Recoger resultados, honrar el trabajo y compartir lo aprendido.",
  "Focus, integrity, and practical strategy.": "Enfoque, integridad y estrategia practica.",
  "Preparation, craft, and building safe spaces.": "Preparacion, oficio y construccion de espacios seguros.",
  "Clarity, simplicity, and faithful light in quiet.": "Claridad, sencillez y una luz fiel en el silencio.",
};

function translateMoonName(name, isSpanish) {
  if (!isSpanish) return name;
  return MOON_NAME_ES[name] || name;
}

function translateMoonMeaning(meaning, isSpanish) {
  if (!isSpanish) return meaning;
  return MOON_MEANING_ES[meaning] || meaning;
}

function looksEnglishMoonGuide(text = "") {
  return /\b(The Full|Full Strawberry Moon|This phase|encourages you|acknowledge|sweetness in your life|moving forward|take advantage|next few days|moon reaches its peak)\b/i.test(
    String(text)
  );
}

function spanishMoonGuideFallback(hero, copy) {
  const moonName = translateMoonName(hero?.name || copy.fullMoon, true);
  const moonDate = hero?.dateUTC ? fmtLocalWithTz(new Date(hero.dateUTC), "es", "a las") : "";
  const meaning = translateMoonMeaning(hero?.meaning || "", true) || copy.fallbackNote;
  return [
    `${moonName}${moonDate ? ` del ${moonDate}` : ""} invita a una revision tranquila de lo que estas cerrando y de lo que quieres cuidar mejor.`,
    `Su tema principal es ${meaning.toLowerCase()} Usa esta energia para agradecer lo que si sostuvo tu camino, soltar compromisos que ya no necesitan tanta atencion y elegir una accion pequena que devuelva orden a tu dia. Si has estado cargando demasiadas expectativas, este momento te pide separar lo urgente de lo importante y mirar con mas calma donde se esta yendo tu energia.`,
    "Durante los proximos dias, escribe una lista breve de asuntos abiertos. Marca cuales requieren una conversacion, cuales necesitan descanso y cuales pueden cerrarse con una decision sencilla. No busques hacerlo todo perfecto. Busca hacerlo con honestidad. Despues elige solo un asunto para atender primero. La claridad aparece mejor cuando reduces el ruido y conviertes una emocion grande en una tarea pequena, concreta y posible.",
    "Si aparece tension emocional, vuelve a lo practico: agua, descanso, una caminata corta, una nota escrita o una conversacion pausada. La guia de esta luna no es presion para cambiarlo todo. Es una invitacion a cerrar ciclos con cuidado y proteger tu energia. Antes de responder desde la urgencia, preguntate si estas actuando desde claridad o desde cansancio. Si no puedes distinguirlo, espera un poco y vuelve a mirar.",
    "Tambien puedes usar esta luna para revisar tus limites. Observa donde dices que si por costumbre, donde aceptas mas de lo que puedes sostener y donde necesitas pedir algo de manera directa. Un limite sano no tiene que sonar duro. Puede ser una pausa antes de responder, una frase simple o una decision de no explicar demasiado. La luna llena favorece mirar lo que ya llego a su punto maximo y permitir que algo termine sin convertirlo en culpa.",
    "Para cerrar el dia, elige un pequeno ritual practico. Ordena tu mesa, guarda una nota, tira una lista vieja, prepara ropa limpia o escribe una frase que nombre lo que estas dejando atras. Despues nombra una cosa que quieres cuidar de aqui en adelante. No necesitas una transformacion enorme para honrar esta luna. Necesitas una decision honesta que puedas sostener manana.",
    "Si compartes espacio con otras personas, no conviertas esta guia en una exigencia silenciosa. Di lo que necesitas con palabras sencillas. Pide tiempo, pide claridad o pide una pausa si hace falta. Si el tema es interno, tratate con la misma honestidad. Reconoce que has cambiado en algunos aspectos y que ciertas formas antiguas de responder ya no cuidan tu paz.",
    "El mejor uso de esta guia es practico: cerrar un ciclo con respeto, agradecer lo que tuvo valor y proteger tu energia para lo que viene. No necesitas una decision perfecta. Necesitas un paso honesto, claro y sostenible. Si vuelves a lo simple, la claridad aparece con menos ruido: que se cierra, que se agradece, que se conserva y que accion pequena hace que tu vida se sienta un poco mas tuya.",
  ].join("\n\n");
}

function fmtLocalWithTz(date, locale, joiner = "at") {
  const dtf = new Intl.DateTimeFormat(locale, {
    year: "numeric", month: "long", day: "numeric",
    hour: "numeric", minute: "2-digit", timeZoneName: "short",
  });
  const parts = dtf.formatToParts(date);
  const dayIdx = parts.findIndex((p) => p.type === "day");
  const timeIdx = parts.findIndex((p) => p.type === "hour");
  const labelDate = parts.slice(0, dayIdx + 1).map((p) => p.value).join("");
  const labelTime = parts.slice(timeIdx).map((p) => p.value).join("");
  return `${labelDate} ${joiner} ${labelTime}`;
}
function monthLabelUTC(d, locale) {
  return new Intl.DateTimeFormat(locale, { month: "long", timeZone: "UTC" }).format(d);
}

/* ===============================
   Component
================================ */
export default function FullMoon() {
  const [selectedYear, setSelectedYear] = useState(() => new Date().getFullYear());
  const lang = currentLanguage();
  const isSpanish = lang === "es";
  const locale = isSpanish ? "es" : undefined;
  const copy = useMemo(
    () =>
      isSpanish
        ? {
        fullMoon: "Luna llena",
        lunarHighlight: "Guia lunar",
        loadingHero: "La luna llena ilumina el camino con una guia serena.",
        season: "temporada",
        timesShown: "Los horarios se muestran en tu zona horaria",
        moonGuide: "Guia lunar",
        preparing: "Espera un momento. Estamos preparando tu guia...",
        fallbackNote:
          "La luna llena de esta noche invita a una revision honesta contigo. Manten los planes simples, da un paso concreto y protege un espacio para descansar.",
        calendar: "Calendario",
        calendarTitle: "Calendario de lunas llenas",
        calendarIntro: "Nombres tradicionales, lunas azules y horarios exactos.",
        thisYear: "Este ano",
        viewYear: "Ver",
        blueMoon: "Luna azul",
        supermoon: "Superluna",
        currentHighlight: "Guia actual",
        nextFullMoon: "Proxima luna llena",
        loadError: "No se pudo calcular el calendario de lunas llenas.",
        shortGuideError: "La guia fue mas corta de lo esperado. Actualiza la pagina.",
        pageTitle: `Calendario de luna llena ${selectedYear} - nombres, fechas, significados y guia mensual`,
        metaDescription: `Consulta el calendario de lunas llenas de ${selectedYear}: nombres tradicionales, lunas azules cuando ocurren y horarios astronomicos exactos en tu zona horaria.`,
        keywords: `luna llena, calendario lunar ${selectedYear}, luna azul, superluna, nombres de luna, significado de la luna, fechas lunares ${selectedYear}, guia lunar, fases lunares, astrologia, astronomia`,
        breadcrumbHome: "Inicio",
          }
        : {
        fullMoon: "Full Moon",
        lunarHighlight: "Lunar Highlight",
        loadingHero: "The full moon lights the path with calm guidance.",
        season: "season",
        timesShown: "Times shown in your timezone",
        moonGuide: "Moon Guide",
        preparing: "Wait a moment please. Preparing your guide...",
        fallbackNote:
          "Tonight's full moon invites a clear check-in with yourself. Keep plans simple, keep actions small and keep rest on the schedule. If life feels noisy, set a short boundary window and return to one helpful task. The goal is steadiness, not perfection.",
        calendar: "Calendar",
        calendarTitle: "Full Moon Calendar",
        calendarIntro: "Traditional names, Blue Moons and exact times.",
        thisYear: "This Year",
        viewYear: "View",
        blueMoon: "Blue Moon",
        supermoon: "Supermoon",
        currentHighlight: "Current highlight",
        nextFullMoon: "Next full moon",
        loadError: "Could not compute full moons.",
        shortGuideError: "Guide was shorter than expected. Please refresh.",
        pageTitle: `Full Moon Calendar ${selectedYear} - Names, Dates, Meanings & Monthly Guide`,
        metaDescription: `See the full moon calendar for ${selectedYear}: traditional names, Blue Moons when they occur and exact astronomical times (shown in your timezone).`,
        keywords: `full moon, full moon calendar ${selectedYear}, blue moon, supermoon, lunar calendar, zodiac full moon, moon names, moon meaning, moon dates ${selectedYear}, moon guide, lunar phases, astrology, astronomy times`,
        breadcrumbHome: "Home",
          },
    [isSpanish, selectedYear]
  );

  const [calendar, setCalendar] = useState([]);
  const [heroActive, setHeroActive] = useState(false);
  const [nextFM, setNextFM] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [heroNote, setHeroNote] = useState("");
  const [noteLoading, setNoteLoading] = useState(false);

  const tzLabel = useMemo(() => {
    try { return Intl.DateTimeFormat().resolvedOptions().timeZone || "local time"; }
    catch { return "local time"; }
  }, []);

  // Fetch year data
  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        setLoading(true); setLoadError("");
        const res = await fetch(apiUrlWithLang(`${API_BASE}/api/fullmoons?year=${selectedYear}`));
        const data = await res.json();
        if (cancel) return;
        if (!res.ok) throw new Error(data?.message || `HTTP ${res.status}`);
        const cal = (data.calendar || []).map((x) => ({ ...x, date: new Date(x.dateUTC) }));
        setCalendar(cal);
        setHeroActive(!!data.heroActive);
        setNextFM(data.next ? { ...data.next, date: new Date(data.next.dateUTC) } : null);
      } catch (e) {
        if (!cancel) { setLoadError(copy.loadError); setCalendar([]); setNextFM(null); setHeroActive(false); }
      } finally { if (!cancel) setLoading(false); }
    })();
    return () => { cancel = true; };
  }, [selectedYear, lang, copy.loadError]);

  const items = useMemo(() => {
    return calendar.map((m) => ({
      ...m,
      zodiac: zodiacForDate(new Date(m.dateUTC)),
      monthIdx: (m.month || new Date(m.dateUTC).getUTCMonth() + 1) - 1,
    }));
  }, [calendar]);

  const heroIndex = useMemo(() => {
    if (!items.length) return -1;
    if (nextFM) {
      const ix = items.findIndex((x) => x.dateUTC === nextFM.dateUTC);
      if (ix >= 0) return ix;
    }
    const now = new Date();
    const futureIx = items.findIndex((x) => new Date(x.dateUTC) >= now);
    return futureIx >= 0 ? futureIx : items.length - 1;
  }, [items, nextFM]);

  const hero = items[heroIndex] || null;

  // Long note for hero Full Moon: read from API { note }, with localStorage v2 and min-length check
  useEffect(() => {
    let cancel = false;
    if (!hero) return;

    (async () => {
      try {
        setNoteLoading(true);
        const lang = currentLanguage();
        const cacheKey = `tf_fm_long_v3_${lang}_${hero.dateUTC}`;
        const cached = localStorage.getItem(cacheKey);
        if (cached) {
          const parsed = JSON.parse(cached);
          if (parsed?.note && wc(parsed.note) >= 500 && !(isSpanish && looksEnglishMoonGuide(parsed.note))) {
            if (!cancel) {
              setHeroNote(parsed.note);
              setNoteLoading(false);
              return;
            }
          } else {
            localStorage.removeItem(cacheKey);
          }
        }

        const res = await fetch(`${API_BASE}/api/moon-blessing`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(withLanguage({
            zodiac: hero.zodiac || "",
            moonName: translateMoonName(hero.name || "Full Moon", isSpanish),
            fullMoonDateUTC: hero.dateUTC,
            meaning: translateMoonMeaning(hero.meaning || "", isSpanish),
            tone: "gentle",
          })),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data?.message || `HTTP ${res.status}`);
        const note = data?.note || "";

        if (wc(note) < 500) throw new Error(copy.shortGuideError);
        if (isSpanish && looksEnglishMoonGuide(note)) throw new Error(copy.shortGuideError);

        if (!cancel) setHeroNote(note);
        try { localStorage.setItem(cacheKey, JSON.stringify({ note })); } catch {}
      } catch {
        if (!cancel) {
          setHeroNote(isSpanish ? spanishMoonGuideFallback(hero, copy) : copy.fallbackNote);
        }
      } finally { if (!cancel) setNoteLoading(false); }
    })();

    return () => { cancel = true; };
  }, [hero, lang, isSpanish, copy.fallbackNote, copy.shortGuideError]);

  /* =========================
     SEO (About.js-style via <Seo />)
  ========================= */
  const siteUrl = "https://tarotfate.com";
  const pageUrl = `${siteUrl}${isSpanish ? "/es/full-moon" : "/full-moon"}`;
  const ogImage = `${siteUrl}/icons/og-cover.jpg`; // 1200x630 social cover
  const pageTitle = copy.pageTitle;
  const metaDescription = copy.metaDescription;
  const nowIso = new Date().toISOString();

  const pageKeywords = copy.keywords.replace(/\s+/g, " ").trim();

  const schemaGraph = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": `${siteUrl}#org`,
        name: "TarotFate",
        url: siteUrl,
        logo: {
          "@type": "ImageObject",
          url: `${siteUrl}/icons/logo.webp`,
          width: 512,
          height: 512
        }
      },
      {
        "@type": "WebSite",
        "@id": `${siteUrl}#website`,
        url: siteUrl,
        name: "TarotFate",
        publisher: { "@id": `${siteUrl}#org` },
        inLanguage: lang
      },
      {
        "@type": "WebPage",
        "@id": `${pageUrl}#webpage`,
        url: pageUrl,
        name: pageTitle,
        description: metaDescription,
        isPartOf: { "@id": `${siteUrl}#website` },
        primaryImageOfPage: {
          "@type": "ImageObject",
          "@id": `${pageUrl}#image`,
          url: ogImage,
          width: 1200,
          height: 630,
          caption: "TarotFate cover"
        },
        breadcrumb: { "@id": `${pageUrl}#breadcrumbs` },
        dateModified: nowIso,
        inLanguage: lang
      },
      {
        "@type": "BreadcrumbList",
        "@id": `${pageUrl}#breadcrumbs`,
        itemListElement: [
          { "@type": "ListItem", position: 1, name: copy.breadcrumbHome, item: isSpanish ? `${siteUrl}/es` : siteUrl },
          { "@type": "ListItem", position: 2, name: copy.fullMoon, item: pageUrl }
        ]
      }
    ]
  };

  const heroPalette = MOON_PALETTES[Math.max(0, hero?.monthIdx ?? new Date().getUTCMonth())];

  return (
    <div className={`${FONT} min-h-screen relative overflow-hidden text-white`} style={{ background: CANVAS_BG }}>
      {/* SEO */}
      <Seo
        title={pageTitle}
        description={metaDescription}
        url={pageUrl}
        image={ogImage}
        schema={schemaGraph}
        keywords={pageKeywords}
        includeMega={true}
      />

      {/* Ambient auras */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-1/3 left-1/2 -translate-x-1/2 w-[1200px] h-[1200px] rounded-full opacity-25 blur-[160px] bg-[radial-gradient(circle,rgba(59,198,214,0.55)_0%,transparent_70%)]" />
        <div className="absolute -bottom-[25%] right-[5%] w-[700px] h-[700px] rounded-full opacity-20 blur-[140px] bg-[radial-gradient(circle,rgba(255,196,86,0.45)_0%,transparent_70%)]" />
      </div>

      {/* HERO */}
      <section className={`pt-6 ${WRAP} ${MAXW}`}>
        <div className={`${PANEL_BASE} p-6 sm:p-8 ${heroPalette.glow}`} style={{ background: heroPalette.bg }} aria-live="polite">
          {/* decorative star wash */}
          <div className="pointer-events-none absolute inset-0 opacity-10">
            <div className="absolute -top-10 left-10 w-72 h-72 rounded-full blur-[90px] bg-white/30" />
            <div className="absolute bottom-0 right-8 w-80 h-80 rounded-full blur-[110px] bg-white/25" />
          </div>

          <div className="relative z-[1] text-center">
            <p className="uppercase tracking-[0.25em] text-[11px] text-white/85">{copy.lunarHighlight}</p>
            <h1 className="mt-1 text-2xl sm:text-3xl font-bold flex items-center justify-center gap-2">
              <span role="img" aria-label={copy.fullMoon}></span>
              {loading ? copy.fullMoon : translateMoonName(hero?.name, isSpanish) || copy.fullMoon}
            </h1>

            <div className="flex items-center justify-center my-2">
              <div className={`h-px w-1/5 bg-gradient-to-r from-transparent ${hero ? Z_ACCENT[hero.zodiac]?.bar : "via-white/60"} to-transparent`} />
              <span className="px-3 text-white/90">✦</span>
              <div className={`h-px w-1/5 bg-gradient-to-r from-transparent ${hero ? Z_ACCENT[hero.zodiac]?.bar : "via-white/60"} to-transparent`} />
            </div>

            <p className="text-base text-white/95">
              {loading ? copy.loadingHero :
                hero ? `${fmtLocalWithTz(new Date(hero.dateUTC), locale, isSpanish ? "a las" : "at")} \u2022 ${isSpanish ? ZODIAC_ES[hero.zodiac] || hero.zodiac : hero.zodiac} ${copy.season}` : "-"}
            </p>
            {!loading && <p className="text-[12px] text-white/80 mt-1">{copy.timesShown}</p>}

            {/* meaning */}
            <div className="mt-3">
              <div className="h-px w-32 mx-auto bg-gradient-to-r from-transparent via-white/50 to-transparent" />
              <p className={`${TEXT} text-[15px] md:text-[16px] leading-relaxed mt-3`}>
                {loading ? " " : translateMoonMeaning(hero?.meaning || "", isSpanish)}
              </p>
              <div className="h-px w-24 mx-auto mt-3 bg-gradient-to-r from-transparent via-white/40 to-transparent" />
            </div>

            {/* guide note */}
            <div className="mt-5">
              <div className="bg-white/10 border border-white/20 rounded-xl p-5 max-w-3xl mx-auto backdrop-blur-[1px] text-sm leading-7 text-left">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-semibold text-white/95">{copy.moonGuide}</h3>
                  <div className={`h-px flex-1 mx-3 bg-gradient-to-r from-transparent ${hero ? Z_ACCENT[hero.zodiac]?.bar : "via-white/50"} to-transparent`} />
                </div>
                {noteLoading
                  ? <span className="animate-pulse text-white/75">{copy.preparing}</span>
                  : <span className="whitespace-pre-line">{heroNote || copy.fallbackNote}</span>}
              </div>
            </div>

            {!!loadError && <p className="mt-3 text-[12px] text-white/80">{loadError}</p>}
          </div>

          {/* header ridge */}
          <div className={`absolute inset-x-0 top-0 h-px opacity-80 bg-gradient-to-r from-transparent ${hero ? MOON_PALETTES[hero.monthIdx].ridge : "via-white/60"} to-transparent`} />
        </div>
      </section>

      {/* YEAR CONTROLS + CALENDAR */}
      <section className={`mt-6 ${WRAP} ${MAXW}`}>
        <div className={`${PANEL_BASE} p-6 sm:p-8 bg-[linear-gradient(135deg,rgba(20,33,61,0.86),rgba(17,51,89,0.86))]`}>
          <div className="relative z-[1]">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
              <div>
                <p className="uppercase tracking-[0.25em] text-[11px] text-white/85">{copy.calendar}</p>
                <h2 className="text-lg sm:text-xl font-semibold text-white/95">{copy.calendarTitle} {selectedYear}</h2>
                <p className={`${TEXT} text-[15px] leading-relaxed mt-1`}>{copy.calendarIntro}</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setSelectedYear((y) => y - 1)}
                  className="tf-outline-button"
                  aria-label={`${copy.viewYear} ${selectedYear - 1}`}
                >
                  ← {selectedYear - 1}
                </button>
                <button
                  onClick={() => setSelectedYear(() => new Date().getFullYear())}
                  className="tf-outline-button"
                  aria-label={copy.thisYear}
                >
                  {copy.thisYear}
                </button>
                <button
                  onClick={() => setSelectedYear((y) => y + 1)}
                  className="tf-outline-button"
                  aria-label={`${copy.viewYear} ${selectedYear + 1}`}
                >
                  {selectedYear + 1} →
                </button>
              </div>
            </div>

            {/* Section divider */}
            <div className="mt-4 h-px w-full bg-gradient-to-r from-transparent via-white/40 to-transparent" />

            {/* Grid */}
            <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {(loading ? Array.from({ length: 6 }) : items).map((m, idx) => {
                if (loading) {
                  return (
                    <div key={`sk-${idx}`} className="rounded-2xl border border-white/10 ring-1 ring-white/10 bg-[linear-gradient(135deg,rgba(20,33,61,0.65),rgba(17,51,89,0.65))] p-4 animate-pulse">
                      <div className="h-2.5 w-24 bg-white/20 rounded mb-3" />
                      <div className="h-4 w-48 bg-white/25 rounded mb-2" />
                      <div className="h-px w-full bg-white/15 my-2" />
                      <div className="h-16 w-full bg-white/10 rounded" />
                    </div>
                  );
                }

                const isHero = hero && m.dateUTC === hero.dateUTC;
                const pal = MOON_PALETTES[m.monthIdx];
                const labelMonth = monthLabelUTC(new Date(m.dateUTC), locale);
                const z = Z_ACCENT[m.zodiac] || { chip: "bg-white/10 text-white border-white/20", bar: "via-white/50" };

                return (
                  <div
                    key={m.dateUTC}
                    className={[
                      "relative rounded-2xl transition will-change-transform hover:-translate-y-1",
                      "border border-white/10 ring-1 ring-white/10",
                      isHero ? "ring-2 ring-white/30" : "",
                      pal.glow,
                    ].join(" ")}
                    style={{ background: pal.bg }}
                  >
                    {/* top twin ridges */}
                    <div className={`h-[9px] w-full bg-gradient-to-r from-transparent ${pal.ridge} to-transparent opacity-80 rounded-t-2xl`} />
                    <div className={`h-px w-full bg-gradient-to-r from-transparent ${z.bar} to-transparent opacity-80`} />

                    {/* subtle stars */}
                    <div className="pointer-events-none absolute inset-0 opacity-10">
                      <div className="absolute -top-6 right-10 w-40 h-40 rounded-full blur-[80px] bg-white/30" />
                    </div>

                    <div className="p-4 relative z-[1]">
                      {/* header row */}
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <div className="text-[11px] uppercase tracking-widest text-white/85">{labelMonth}</div>
                          <div className="mt-1 text-lg font-semibold flex flex-wrap items-center gap-2 text-white">
                            {translateMoonName(m.name, isSpanish)}
                            {m.blueMoon && (
                              <span className={`text-[10px] font-medium rounded-full px-2 py-[2px] border ${z.chip}`}>{copy.blueMoon}</span>
                            )}
                            {m.supermoon && (
                              <span className={`text-[10px] font-medium rounded-full px-2 py-[2px] border ${z.chip}`}>{copy.supermoon}</span>
                            )}
                          </div>
                        </div>
                        <div className="text-right text-xs sm:text-sm text-white/85">
                          <span className={`inline-block rounded-full px-2 py-[2px] border ${z.chip}`}>{isSpanish ? ZODIAC_ES[m.zodiac] || m.zodiac : m.zodiac}</span>
                        </div>
                      </div>

                      {/* rule */}
                      <div className="mt-3 h-px w-full bg-white/20" />

                      {/* datetime */}
                      <div className="mt-2 text-white/95">{fmtLocalWithTz(new Date(m.dateUTC), locale, isSpanish ? "a las" : "at")}</div>

                      {/* mini rule */}
                      <div className={`mt-2 h-px w-24 bg-gradient-to-r from-transparent ${z.bar} to-transparent`} />

                      {/* meaning */}
                      <p className="mt-2 text-white/90 leading-7">{translateMoonMeaning(m.meaning, isSpanish)}</p>

                      {/* highlight chip */}
                      {isHero && (
                        <div className={`mt-3 inline-flex items-center rounded-full px-3 py-1 text-xs font-medium border ${z.chip}`}>
                          {heroActive ? copy.currentHighlight : copy.nextFullMoon}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* top gleam */}
          <div className="absolute inset-x-0 top-0 h-px opacity-70 bg-gradient-to-r from-transparent via-white/60 to-transparent" />
        </div>
      </section>
    </div>
  );
}
